import { useState, useEffect } from "react";
import { useAssets } from "@/contexts/AssetContext.jsx";
import { useDowntime } from "@/contexts/DowntimeContext.jsx";
import { getTimeAgo } from "@/lib/datetime-utils.js";
import { Clock } from "lucide-react";

/**
 * Component that displays "Last updated X minutes ago" counter
 * Counts up from the last data refresh time
 * @param {Object} props - Component props
 * @param {string} props.context - Context to use for last updated time ('assets' or 'downtime')
 * @returns {JSX.Element} Last updated counter component
 */
export default function LastUpdatedCounter({ context = "assets" }) {
  const { lastUpdatedTime: assetsLastUpdated } = useAssets();
  const { lastUpdatedTime: downtimeLastUpdated } = useDowntime();

  const lastUpdatedTime =
    context === "downtime" ? downtimeLastUpdated : assetsLastUpdated;
  const [, setForceUpdate] = useState(0);

  // Update the display every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setForceUpdate((prev) => prev + 1);
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  if (!lastUpdatedTime) {
    return (
      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
        <Clock className='h-4 w-4 shrink-0' />
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <div className='flex items-center gap-2 text-sm text-muted-foreground'>
      <Clock className='h-4 w-4 shrink-0' />
      <span>Last updated {getTimeAgo(lastUpdatedTime)}</span>
    </div>
  );
}
